import { createSlice } from "@reduxjs/toolkit";
// Import from the small dependency-free module, NOT from utils/helpers: the
// helpers barrel drags in the component tree and creates an import cycle back
// into this reducer tree (see src/utils/randomBrightColor.js header).
import { getRandomBrightColor } from "../../utils/randomBrightColor";

const initialState = {
  wheel: {
    color: [],
    image: [],
    prompt: [],
    scheme: "auto",
    mood: "auto",
    recent: [],
    saved: [],
    baseColor: getRandomBrightColor(),
  },
  segment: {
    color: [],
    image: [],
    prompt: [],
    scheme: "auto",
    mood: "auto",
    recent: [],
    saved: [],
  },
  selectedPaletteTab: "0",
  selectedWheelPalette:null,
  selectedSegmentPalette:null,
  selectedSegmentColor:null,
};

const colorPaletteSlice = createSlice({
  name: "colorPalette",
  initialState,
  reducers: {
    setColorPaletteDefault: (state, action) => {
      const { paletteType = "segment" } = action.payload;
      state[paletteType] = {
        ...initialState[paletteType],
      };
      // Reset tab to default (first tab - Prompt)
      state.selectedPaletteTab = "0";
    },

    setBaseColor: (state, action) => {
      const { color, paletteType = "wheel" } = action.payload;
      state[paletteType].baseColor = color;
    },

    setColorMood: (state, action) => {
      const { mood, paletteType = "segment" } = action.payload;
      state[paletteType].mood = mood;
    },

    setColorScheme: (state, action) => {
      const { scheme, paletteType = "segment" } = action.payload;
      state[paletteType].scheme = scheme;
    },

    setSelectedPalette: (state, action) => {
      const { palette, paletteType = "segment" } = action.payload;
      state[paletteType].selectedPalette = palette;
      state[paletteType].recent = [...state[paletteType].recent, palette];
    },

    addColorPalettes: (state, action) => {
      const { paletteColors, type, paletteType = "segment" } = action.payload;
      state[paletteType][type] = paletteColors;
    },

    updateColorPalette: (state, action) => {
      const {
        paletteId,
        updatedPalette,
        type,
        paletteType = "segment",
      } = action.payload;
      state[paletteType][type] = state[paletteType][type].map((palette) =>
        palette.id === paletteId ? updatedPalette : palette
      );
    },

    selectPalette: (state, action) => {
      const { paletteId, type, paletteType = "segment" } = action.payload;
      state[paletteType][type] = state[paletteType][type].map((palette) => ({
        ...palette,
        selected: palette.id === paletteId,
      }));
    },

    updateColorInPalette: (state, action) => {
      const {
        paletteId,
        colorId,
        updatedColor,
        type,
        paletteType = "segment",
      } = action.payload;
      state[paletteType][type] = state[paletteType][type].map((palette) =>
        palette.id === paletteId
          ? {
              ...palette,
              colors: palette.colors.map((color) =>
                color.id === colorId ? { ...color, ...updatedColor } : color
              ),
            }
          : palette
      );
    },

    lockColor: (state, action) => {
      const { colorId, paletteType = "segment", type } = action.payload;
      if (!state[paletteType]) return;
      state[paletteType][type] = state[paletteType][type].map((palette) => ({
        ...palette,
        colors: palette.colors.map((color) => ({
          ...color,
          locked: color.id === colorId ? !color.locked : color.locked,
        })),
      }));
    },

    selectColor: (state, action) => {
      const { colorId, paletteType = "segment", type } = action.payload;
      if (!state[paletteType]) return;
      state[paletteType][type] = state[paletteType][type].map((palette) => ({
        ...palette,
        colors: palette.colors.map((color) => ({
          ...color,
          selected: color.id === colorId,
        })),
      }));
    },

    editPaletteTitle: (state, action) => {
      const {
        paletteId,
        newTitle,
        paletteType = "segment",
        type,
      } = action.payload;
      if (!state[paletteType]) return;
      state[paletteType][type] = state[paletteType][type].map((palette) =>
        palette.id === paletteId
          ? { ...palette, paletteName: newTitle }
          : palette
      );
    },

    setSelectedPaletteTab: (state, action) => {
      state.selectedPaletteTab = action.payload;
    },

    setSelectedWheelPalette: (state, action) => {
      state.selectedWheelPalette = action.payload;
    },

    setSelectedSegmentPalette: (state, action) => {
      state.selectedSegmentPalette = action.payload;
    },

    setSelectedSegmentColor: (state, action) => {
      state.selectedSegmentColor = action.payload;
    },
  },
});

export const {
  setColorPaletteDefault,
  setSelectedPalette,
  setColorMood,
  setColorScheme,
  selectPalette,
  updateColorPalette,
  selectColor,
  lockColor,
  addColorPalettes,
  updateColorInPalette,
  editPaletteTitle,
  setBaseColor,
  setSelectedPaletteTab,
  setSelectedWheelPalette,
  setSelectedSegmentPalette,
  setSelectedSegmentColor,
} = colorPaletteSlice.actions;

export default colorPaletteSlice.reducer;
